import { create } from 'zustand';
import apiClient from '@/services/letsparkyClient';
import { 
  Device as ApiDevice, 
  Parking as ApiParking,
  User as ApiUser,
  PaginatedResponse, 
  ListResponse,
  handleApiError 
} from '@/types/api';
import { Parking, ParkingStatus } from '@letsparky/api-v2-client';

export interface DashboardStats {
  users: number;
  tenants: number;
  devices: number;
  parkings: number;
  payments: number;
}

export interface RevenueStats {
  today: number;
  todayDuration: number;
  weekly: number;
  weeklyDuration: number;
  monthly: number;
  monthlyDuration: number;
  total: number;
  totalDuration: number;
}

export interface ParkingSession extends Omit<ApiParking, 'status'> {
  status: string; // Ensure we have a string status field
}

export interface Device extends Omit<ApiDevice, 'status'> {
  // Additional fields or modifications if needed
}

export interface SystemHealth {
  apiUptime: number;
  performance: number;
  databaseStatus: string;
  paymentGatewayStatus: string;
  mqttStatus: string;
  responseTime: number;
  errorRate: number;
  successRate: number;
}

export interface DeviceHealth {
  totalDevices: number;
  onlineDevices: number;
  offlineDevices: number;
  alarmedDevices: number;
  blockedDevices: number;
  avgBattery: number;
  alarmRate: number;
  uptime: number;
}

export interface ActivityItem {
  id: string;
  icon: string;
  title: string;
  description: string;
  time: string;
  type: 'info' | 'success' | 'warning' | 'error';
  avatar?: string;
}

interface DashboardState {
  // Stats
  stats: DashboardStats | null;
  isLoadingStats: boolean;
  statsError: string | null;
  
  // Parking sessions
  parkingSessions: ParkingSession[];
  isLoadingParkingSessions: boolean;
  parkingSessionsError: string | null;
  
  // Devices
  devices: Device[];
  isLoadingDevices: boolean;
  devicesError: string | null;
  
  // System Health
  systemHealth: SystemHealth | null;
  isLoadingSystemHealth: boolean;
  systemHealthError: string | null;
  
  // Device Health
  deviceHealth: DeviceHealth | null;
  isLoadingDeviceHealth: boolean;
  deviceHealthError: string | null;
  
  // Recent Activities
  recentActivities: ActivityItem[];
  isLoadingActivities: boolean;
  activitiesError: string | null;
  
  // Actions
  fetchStats: (isSystem: boolean, tenantId?: string) => Promise<void>;
  fetchParkingSessions: (status: string) => Promise<void>;
  fetchDevices: () => Promise<void>;
  fetchSystemHealth: () => Promise<void>;
  fetchDeviceHealth: () => Promise<void>;
  fetchRecentActivities: () => Promise<void>;
  fetchAllDashboardData: (isSystem: boolean, tenantId?: string) => Promise<void>;
}

export const useDashboardStore = create<DashboardState>()((set, get) => ({
  // Stats initial state
  stats: null,
  isLoadingStats: false,
  statsError: null,
  
  // Parking sessions initial state
  parkingSessions: [],
  isLoadingParkingSessions: false,
  parkingSessionsError: null,
  
  // Devices initial state
  devices: [],
  isLoadingDevices: false,
  devicesError: null,
  
  // System Health initial state
  systemHealth: null,
  isLoadingSystemHealth: false,
  systemHealthError: null,
  
  // Device Health initial state
  deviceHealth: null,
  isLoadingDeviceHealth: false,
  deviceHealthError: null,
  
  // Recent Activities initial state
  recentActivities: [],
  isLoadingActivities: false,
  activitiesError: null,
  
  // Fetch dashboard stats
  fetchStats: async (isSystem: boolean, tenantId?: string) => {
    set({ isLoadingStats: true, statsError: null });
    try {
      if (isSystem) {
        const [usersRes, tenantsRes, devicesRes, parkingsRes, paymentsRes] = await Promise.all([
          apiClient.users.listPaginated({ page: 1, limit: 1 }), 
          apiClient.tenants.listPaginated({ page: 1, limit: 1 }),
          apiClient.devices.listPaginated({ page: 1, limit: 1 }),
          apiClient.parkings.listPaginated({ page: 1, limit: 1 }),
          apiClient.payments.listPaginated({ page: 1, limit: 1 })
        ]);

        set({
          stats: {
            users: usersRes.pagination.total || 0,
            tenants: tenantsRes.pagination.total || 0,
            devices: devicesRes.pagination.total || 0,
            parkings: parkingsRes.pagination.total || 0,
            payments: paymentsRes.pagination.total || 0
          },
          isLoadingStats: false,
        });
      } else {
        if (!tenantId) {
          throw new Error('Tenant ID is required for non-system users');
        }

        const [usersRes, devicesRes, parkingsRes, paymentsRes] = await Promise.all([
          apiClient.users.list({ tenant_id: tenantId }) as unknown as ListResponse<ApiUser>,
          apiClient.devices.listPaginated({ page: 1, limit: 1 }, { tenant_id: tenantId }),
          apiClient.parkings.listPaginated({ page: 1, limit: 1 }, { tenant_id: tenantId }),
          apiClient.payments.listPaginated({ page: 1, limit: 1 }, { tenant_id: tenantId }),
        ]);

        console.log(usersRes, parkingsRes)
        set({
          stats: {
            users: usersRes.total || 0,
            tenants: 0,
            devices: devicesRes.pagination.total || 0,
            parkings: parkingsRes.pagination.total || 0,
            payments: paymentsRes.pagination.total || 0
          },
          isLoadingStats: false,
        });
      }
    } catch (error) {
      set({ 
        statsError: handleApiError(error),
        isLoadingStats: false
      });
    }
  },
  
  // Fetch parking sessions
  fetchParkingSessions: async (status: string) => {
    set({ isLoadingParkingSessions: true, parkingSessionsError: null });
    try {
      const response = await apiClient.parkings.list({ status: status as ParkingStatus });
      
      if (!response) {
        throw new Error('No data returned from API');
      }
      console.log("FETCH", response)
      
      set({
        parkingSessions: response || [],
        isLoadingParkingSessions: false
      });
    } catch (error) {
      set({
        parkingSessionsError: handleApiError(error),
        isLoadingParkingSessions: false
      });
    }
  },
  
  // Fetch devices
  fetchDevices: async () => {
    set({ isLoadingDevices: true, devicesError: null });
    try {
      const response = await apiClient.devices.list();
      
      if (!response) {
        throw new Error('No data returned from API');
      }
      
      set({
        devices: response || [],
        isLoadingDevices: false
      });
    } catch (error) {
      set({
        devicesError: handleApiError(error),
        isLoadingDevices: false
      });
    }
  },
  
  // Fetch system health metrics
  fetchSystemHealth: async () => {
    set({ isLoadingSystemHealth: true, systemHealthError: null });
    try {
      // For now, we'll use mock data since there might not be a dedicated system health endpoint
      // This could be replaced with real API calls when available
      const mockSystemHealth: SystemHealth = {
        apiUptime: 98.5,
        performance: 87,
        databaseStatus: 'healthy',
        paymentGatewayStatus: 'online',
        mqttStatus: 'connected',
        responseTime: 120,
        errorRate: 0.8,
        successRate: 99.2
      };
      
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 500));
      
      set({
        systemHealth: mockSystemHealth,
        isLoadingSystemHealth: false
      });
    } catch (error) {
      set({
        systemHealthError: handleApiError(error),
        isLoadingSystemHealth: false
      });
    }
  },
  
  // Fetch device health metrics
  fetchDeviceHealth: async () => {
    set({ isLoadingDeviceHealth: true, deviceHealthError: null });
    try {
      const devices = get().devices;
      
      // If devices aren't loaded yet, fetch them
      if (devices.length === 0) {
        await get().fetchDevices();
      }
      
      const currentDevices = get().devices;
      const totalDevices = currentDevices.length;
      
      // Try to get device logs for health calculation
      let deviceHealth: DeviceHealth;
      
      try {
        // Attempt to fetch recent device logs for health metrics
        const last24h = new Date(Date.now() - 24 * 60 * 60 * 1000);
        const deviceLogsResponse = await apiClient.deviceLogs?.list({
          start_time: last24h.getTime(),
          end_time: Date.now(),
          limit: 1000
        });
        
        if (deviceLogsResponse?.data?.data) {
          const logs = deviceLogsResponse.data.data;
          const deviceStates = new Map();
          
          // Get latest state for each device
          logs.forEach((log: any) => {
            const existing = deviceStates.get(log.device_id);
            if (!existing || log.logged_at > existing.logged_at) {
              deviceStates.set(log.device_id, log);
            }
          });
          
          const states = Array.from(deviceStates.values());
          const onlineDevices = states.filter(s => s.status?.toLowerCase() === 'online').length;
          const alarmedDevices = states.filter(s => s.alarm_status).length;
          const blockedDevices = states.filter(s => s.blocked).length;
          const devicesWithBattery = states.filter(s => s.battery);
          const avgBattery = devicesWithBattery.length > 0 
            ? Math.round(devicesWithBattery.reduce((sum: number, d: any) => sum + (d.battery || 0), 0) / devicesWithBattery.length)
            : 0;
          
          deviceHealth = {
            totalDevices,
            onlineDevices,
            offlineDevices: totalDevices - onlineDevices,
            alarmedDevices,
            blockedDevices,
            avgBattery,
            alarmRate: totalDevices > 0 ? Math.round((alarmedDevices / totalDevices) * 100) : 0,
            uptime: totalDevices > 0 ? Math.round((onlineDevices / totalDevices) * 100) : 0,
          };
        } else {
          throw new Error('No device logs available');
        }
      } catch (logError) {
        // Fallback to mock data if device logs aren't available
        const onlineDevices = Math.floor(totalDevices * 0.85); // Assume 85% online
        deviceHealth = {
          totalDevices,
          onlineDevices,
          offlineDevices: totalDevices - onlineDevices,
          alarmedDevices: Math.floor(totalDevices * 0.05), // 5% with alarms
          blockedDevices: Math.floor(totalDevices * 0.02), // 2% blocked
          avgBattery: 3800, // Mock average battery level
          alarmRate: 5,
          uptime: 85,
        };
      }
      
      set({
        deviceHealth,
        isLoadingDeviceHealth: false
      });
    } catch (error) {
      set({
        deviceHealthError: handleApiError(error),
        isLoadingDeviceHealth: false
      });
    }
  },
  
  // Fetch recent activities
  fetchRecentActivities: async () => {
    set({ isLoadingActivities: true, activitiesError: null });
    try {
      const [recentParkings, recentPayments] = await Promise.all([
        apiClient.parkings.listPaginated({ page: 1, limit: 5 }),
        apiClient.payments.listPaginated({ page: 1, limit: 5 })
      ]);

      const activities: ActivityItem[] = [];
      
      // Add parking activities
      recentParkings.data.slice(0, 3).forEach((parking: any, index: number) => {
        activities.push({
          id: `parking-${parking.id || index}`,
          icon: 'FiCheckCircle',
          title: parking.status === 'COMPLETED' ? 'Parking session completed' : 'Parking session started',
          description: `Session for ${parking.license_plate || 'vehicle'} ${parking.status === 'COMPLETED' ? 'completed' : 'started'}`,
          time: new Date(parking.end || parking.start || Date.now()).toLocaleString(),
          type: parking.status === 'COMPLETED' ? 'success' : 'info'
        });
      });

      // Add payment activities
      recentPayments.data.slice(0, 2).forEach((payment: any, index: number) => {
        activities.push({
          id: `payment-${payment.id || index}`,
          icon: 'FiCreditCard',
          title: 'Payment processed',
          description: `₺${payment.amount} payment ${payment.status === 'COMPLETED' ? 'completed successfully' : 'processed'}`,
          time: new Date(payment.created_at || Date.now()).toLocaleString(),
          type: payment.status === 'COMPLETED' ? 'success' : 'info'
        });
      });

      // Sort by time (most recent first)
      activities.sort((a, b) => new Date(b.time).getTime() - new Date(a.time).getTime());
      
      set({
        recentActivities: activities.slice(0, 5), // Keep only 5 most recent
        isLoadingActivities: false
      });
    } catch (error) {
      set({
        activitiesError: handleApiError(error),
        isLoadingActivities: false
      });
    }
  },
  
  // Fetch all dashboard data at once
  fetchAllDashboardData: async (isSystem: boolean, tenantId?: string) => {
    try {
      await Promise.all([
        get().fetchStats(isSystem, tenantId),
        get().fetchDevices(),
        get().fetchParkingSessions('COMPLETED'),
        get().fetchSystemHealth(),
        get().fetchRecentActivities()
      ]);
      
      // Fetch device health after devices are loaded
      await get().fetchDeviceHealth();
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    }
  },
})); 